/**
 * MIRA Tabs Component
 * Wraps Shadcn Tabs with MIRA design system styles
 */

import React from 'react';
import {
  Tabs as ShadcnTabs,
  TabsList as ShadcnTabsList,
  TabsTrigger as ShadcnTabsTrigger,
  TabsContent as ShadcnTabsContent,
} from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import styles from '@/styles/components/layout/tabs.module.css';
import { X } from 'lucide-react';

export const Tabs = ({
  children,
  onClickTab,
  selectedTabId,
  tabs,
  variant = 'primary',
  canClose = false,
  onClose,
  isScrollable = false,
  borderRequired = true,
  className,
  ...props
}) => {
  if (Array.isArray(tabs) && tabs.length > 0) {
    const activeValue = String(
      selectedTabId !== undefined ? selectedTabId : tabs[0]?.id
    );

    const handleValueChange = (value) => {
      const matched = tabs.find((tab) => String(tab.id) === value);
      if (matched && onClickTab) {
        onClickTab(matched);
      }
    };

    return (
      <div className={cn(styles.legacyContainer, className)}>
        <ShadcnTabs
          value={activeValue}
          onValueChange={handleValueChange}
          {...props}
        >
          <div
            className={cn(
              styles.legacyHeader,
              borderRequired && styles.withBorder,
              styles[`header_${variant}`]
            )}
          >
            <ShadcnTabsList
              className={cn(
                styles.legacyList,
                isScrollable && styles.isScrollable,
                styles[`list_${variant}`]
              )}
              style={{ width: 'auto', padding: 0 }}
            >
              {tabs.map((tab, index) => {
                const value = String(tab.id);
                const isActive = value === activeValue;
                const hasError = Boolean(tab.hasError);

                return (
                  <div className={styles.tabWrapper} key={value}>
                    <ShadcnTabsTrigger
                      value={value}
                      data-tab-id={value}
                      disabled={tab.disabled}
                      className={cn(
                        styles.trigger,
                        // `TabsTrigger` ships `rounded-sm`, and `cn` is
                        // tailwind-merge — it can drop a Tailwind class that
                        // another Tailwind class overrides, but it cannot see
                        // through a CSS module, so `.variant_line`'s
                        // `border-radius: 0` won or lost on bundle order. It
                        // lost: 2px of radius on the 2px underline curled its
                        // ends up off the rail, so the active marker read as a
                        // hooked dash instead of a straight edge. Said in
                        // Tailwind, the merge resolves it properly.
                        variant === 'line' && 'rounded-none',
                        styles[`variant_${variant}`],
                        isActive && styles[`variant_${variant}Active`],
                        hasError && styles.triggerError,
                        hasError && isActive && styles.triggerErrorActive
                      )}
                    >
                      {hasError && !isActive && (
                        <span className={styles.errorDot} aria-hidden />
                      )}
                      <span className={styles.triggerLabel}>{tab.name}</span>
                    </ShadcnTabsTrigger>
                    {canClose && tab.id !== tabs[0]?.id && (
                      <button
                        type="button"
                        className={cn(
                          styles.closeButton,
                          isActive && styles.closeButtonActive,
                          hasError && !isActive && styles.closeButtonError
                        )}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.stopPropagation();
                          onClose?.(tab);
                        }}
                        aria-label={`Close ${tab.name}`}
                      >
                        <X className={styles.closeIcon} />
                      </button>
                    )}
                  </div>
                );
              })}
            </ShadcnTabsList>
          </div>
          {children}
        </ShadcnTabs>
      </div>
    );
  }

  return (
    <ShadcnTabs className={className} value={selectedTabId} {...props}>
      {children}
    </ShadcnTabs>
  );
};

export const TabsList = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnTabsList
        ref={ref}
        className={cn(styles.miraTabsList, className)}
        {...props}
      />
    );
  }
);

TabsList.displayName = 'MiraTabsList';

export const TabsTrigger = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnTabsTrigger
        ref={ref}
        className={cn(styles.miraTabsTrigger, className)}
        {...props}
      />
    );
  }
);

TabsTrigger.displayName = 'MiraTabsTrigger';

export const TabsContent = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnTabsContent
        ref={ref}
        className={cn(styles.miraTabsContent, className)}
        {...props}
      />
    );
  }
);

TabsContent.displayName = 'MiraTabsContent';
